<template>
  <Page>
    <ConfirmModal
      v-if="modal"
      :onClose="handleModal"
      :onConfirm="onDelete">
      <template v-slot:header-title>
        <h4>ยืนยันการลบ</h4>
      </template>
      <div>คุณยืนยันที่จะลบคอลเลคชั่นนี้ ?</div>
    </ConfirmModal>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <router-link
            class="btn btn-action btn-action-back"
            :to="getBackURL()">
            <i class="fas fa-chevron-left" />
          </router-link>
          <div class="title-container">
            <div class="subtitle">{{ getCollectionType() }}
              <span class="badge badge-light" v-if="isCollectionDynamic">ไดนามิก</span>
            </div>
            <div class="title">
              <span>{{ getCollectionTitle() }}</span>
              <span class="badge badge-success badge-new" v-if="hasNewItems">ใหม่</span>
            </div>
          </div>
          <div class="actions">
            <small class="text-danger mr-3" v-if="isServerError()">พบข้อผิดพลาดในการบันทึกข้อมูล
              กรุณาลองอีกครั้งหรือติดต่อผู้ดูแลระบบ code :
              {{ errorStatus }}</small>
            <small class="text-danger mr-3" v-if="isError422()">พบข้อผิดพลาดในการบันทึกข้อมูล กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง</small>
            <small
              class="text-success font-weight-bold mr-3"
              v-if="saveCollectionState.isSaved">
              <i class="fa fa-check" /> บันทึกแล้ว
            </small>
            <span v-if="isCollectionSaving">
              <i class="fa fa-spin fa-spinner mr-1" />
              <small class="mr-2">
                กำลังบันทึก
              </small>
            </span>
            <button
              type="submit"
              class="btn btn-outline-dark btn-pinned mr-2"
              @click.stop="onPinCollection">
              <div v-if="collection.isPinned">
                <span class="title">
                  <i class="fas fa-bookmark mr-1"></i><span class="label">คอลเล็คชั่นนี้ถูกปักหมุดอยู่</span>
                </span>
                <span class="hover">
                  <i class="far fa-bookmark mr-1"></i><span class="label">ยกเลิกการปักหมุดคอลเล็คชั่นนี้</span>
                </span>
              </div>
              <div v-if="!collection.isPinned">
                <i class="far fa-bookmark mr-1"></i><span class="label">ปักหมุดคอลเล็คชั่นนี้</span>
              </div>
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="!isValid"
              @click.stop="onSubmit(false)">
              บันทึก
            </button>
            <Dropdown v-if="pageMode !== 'create'" :alignment="'right'">
              <template v-slot:dropdow-button>
                <button
                  type="button"
                  class="btn btn-outline-dark btn-action m-0 ml-2">
                  <i class="fas fa-cog"></i>
                </button>
              </template>
              <div class="dropdown-item" @click.stop="handleModal">
                ลบคอลเลคชั่น
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <div class="row">
        <div class="col-md-4">
          <CollectionInformation
            @onTagChanged="getPreviewCollectionItems"
            :collection="collection"
            :saveCollectionStateError="saveCollectionStateError"/>
        </div>
        <div class="col-md-8">
          <PreloaderSection
            v-if="isLoading" />
          <div
            v-if="!isLoading"
            class="section-container">
            <Section class="section-collection">
              <div class="section-header">
                <h1 class="title">รายการ</h1>
                <div class="selector">
                  <SelectCollectionItem
                    v-if="!isCollectionDynamic && collection.type"
                    @update:collectionItems="updateCollectionItems"
                    :collectionItems="collectionItems"
                    :collectionType="collection.type"/>
                </div>
              </div>
              <div class="section-sort">
                <div class="name">เรียงรายการตาม</div>
                <div class="sort-by">
                  <select
                    v-if="isCollectionDynamic"
                    :disabled="collectionItems && collectionItems.length == 0"
                    class="form-control"
                    v-model="collection.sortOrder"
                    @change="onSortOrder">
                    <option
                      :value="option.key"
                      v-for="(option, optionIndex) in getSortOptions()"
                      :key="optionIndex">{{ option.name }}</option>
                  </select>
                  <select
                    v-else
                    :disabled="collectionItems && collectionItems.length == 0"
                    class="form-control"
                    v-model="collection.sortBy"
                    @change="onSortItems">
                    <option
                      :value="option.key"
                      v-for="(option, optionIndex) in getSortOptions()"
                      :key="optionIndex">{{ option.name }}</option>
                  </select>
                </div>
              </div>
              <draggable
                v-if="storeCollectionItems && storeCollectionItems.length > 0"
                v-model="storeCollectionItems"
                v-bind="dragOptions"
                :disabled="isSortDisabled()">
                <div
                  class="list-item-container no-underline"
                  :class="{ draggable: !isSortDisabled() }"
                  v-for="(item, itemIndex) in collectionItems"
                  :key="itemIndex">
                  <div class="mr-3" v-if="!isSortDisabled()">
                    <i class="fas fa-bars"></i>
                  </div>
                  <div class="list-item-image">
                    <ImageView
                      :source="getCoverImage(item)" />
                  </div>
                  <div class="list-item-detail">
                    <div class="list-item list-item-primary list-item-title">
                      <CollectionItemInformation
                        v-if="item && item[item.type]"
                        :isDragable="!isSortDisabled()"
                        :collectionItem="item"
                        :collectionType="collection.type"/>
                      <div v-else>ไม่มีข้อมูล</div>
                    </div>
                    <div class="list-item list-item-action">
                      <div class="action-container">
                        <a
                          v-if="!isCollectionDynamic"
                          class="btn btn-light btn-action"
                          @click="deleteCollectionItems(item)">
                          <i class="far fa-trash-alt"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </draggable>
              <EmptyMessage
                v-if="!isLoading && collectionItems && collectionItems.length === 0">
                <h3>ไม่มีรายการ</h3>
              </EmptyMessage>
            </Section>
            <div class="pagination-container" v-if="isShowPagination()">
              <Pagination :lastPage="lastPage" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Page>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import dateFormetter from '@/utils/dateFormatter';
import Page from '@/layouts/Page.vue';
import Section from '@/components/Section.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import Dropdown from '@/components/Dropdown.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import Pagination from '@/components/Pagination.vue';
import ImageView from '@/image/ImageView.vue';
import SelectCollectionItem from '@/collection/SelectCollectionItem.vue';
import CollectionInformation from '@/collection/CollectionInformation.vue';
import CollectionItemInformation from '@/collection/CollectionItemInformation.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';

export default {
  mixins: [dateFormetter],
  props: {
    collectionData: {
      type: Object,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      default: () => true,
    },
    lastPage: {
      type: Number,
    },
  },
  components: {
    draggable,
    ImageView,
    Section,
    Page,
    SelectCollectionItem,
    CollectionInformation,
    CollectionItemInformation,
    ConfirmModal,
    Dropdown,
    EmptyMessage,
    Pagination,
    PreloaderSection,
  },
  data() {
    return {
      dragOptions: {
        animation: 500,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      },
      modal: false,
    };
  },
  computed: {
    ...mapGetters(['collection', 'collectionItems', 'isCollectionDynamic', 'collectionLastPage', 'saveCollectionState', 'isCollectionSaving']),
    saveCollectionStateError() {
      return this.$store.state.editingCollection.saveCollectionStateError;
    },
    storeCollectionItems: {
      get() {
        return this.$store.state.editingCollection.collectionItems;
      },
      set(value) {
        this.$store.dispatch('setCollectionItems', { payload: value });
      },
    },
    collectionId() {
      return this.$route.params.collectionId;
    },
    isValid() {
      if (this.isCollectionSaving) {
        return false;
      }
      if (this.collection.type === 'video' && !this.collection.contentCategoryId) {
        return false;
      }
      return this.collection.title && this.validateKey();
    },
    pageMode() {
      return this.collectionId === 'create' ? 'create' : 'edit';
    },
    filter() {
      return this.$route.query;
    },
  },
  created() {
    if (this.pageMode === 'create') {
      if (this.filter && !this.filter.type) {
        this.$store.dispatch('setCollectionAttribute', { key: 'type', value: 'video' });
      }
      this.$store.dispatch('setCollectionAttribute', { key: 'type', value: this.getFilterType(this.filter) });
    }
  },
  methods: {
    getFilterType(filter) {
      return filter.type ? filter.type.toLowerCase() : 'video';
    },
    updateCollectionItems(collectionItem) {
      this.$store.dispatch('setCollectionItems', { payload: collectionItem });
    },
    getSortOptions() {
      const standardSort = [
        {
          key: 'createdAt',
          name: 'วันที่สร้าง',
        },
        {
          key: 'manual',
          name: 'Manual',
        }];
      const dynamicSort = [
        {
          key: 'asc',
          name: 'วันที่สร้าง (เก่าสุดมาก่อน)',
        },
        {
          key: 'desc',
          name: 'วันที่สร้าง (ใหม่สุดมาก่อน)',
        }];
      if (this.isCollectionDynamic) {
        return dynamicSort;
      }
      return standardSort;
    },
    shouldThisOptionDisabled(option) {
      if (this.isCollectionDynamic) {
        return option.disabledOnDynamic;
      }
    },
    async getPreviewCollectionItems(tag) {
      this.$store.commit('setTags', tag);
      this.$store.dispatch('setCollectionDynamicState', 'preview');
      const filter = {
      };
      const params = { filter, collectionId: this.collectionId };
      this.$store.dispatch('loadCollectionItemsById', params);
    },
    getCoverImage(item) {
      return _.get(item[item.type], 'coverImage', null);
    },
    validateKey() {
      return this.collection.key && this.isEnglishOnly(this.collection.key);
    },
    isEnglishOnly(string) {
      return /^[a-zA-Z0-9-]+$/.test(string);
    },
    handleModal() {
      this.modal = !this.modal;
    },
    async onDelete() {
      this.$store.dispatch('deleteCollection', this.collectionId).then(() => {
        const backURL = this.getBackURL();
        this.$router.push(backURL);
      });
    },
    isServerError() {
      return this.errorStatus && this.errorStatus !== 422;
    },
    isError422() {
      return this.errorStatus && this.errorStatus === 422;
    },
    async onSubmit(notRedirect) {
      const backURL = `/collections?type=${this.getRawCollectionType()}`;
      if (this.pageMode === 'create') {
        this.$store.dispatch('createCollection').then(() => {
          if (!notRedirect) {
            this.$router.push(backURL);
          }
        });
      } else {
        this.$store.dispatch('updateCollection');
      }
    },
    transFormCollectionItems() {
      return _.map(this.collectionItems, (item) => item.itemId);
    },
    isSortDisabled() {
      return this.collection.sortBy !== 'manual';
    },
    onPinCollection() {
      this.collection.isPinned = !this.collection.isPinned;
      if (this.pageMode !== 'create') {
        this.onSubmit(true);
      }
    },
    getCollectionTitle() {
      return _.get(this.collection, 'title', this.collectionId);
    },
    onSortItems(e) {
      this.$emit('onSortByChange', e.target.value);
    },
    onSortOrder(e) {
      this.$emit('onSortOrderChange', e.target.value);
    },
    getRawCollectionType() {
      if (this.collection && this.collection.type) {
        const collectionType = this.collection.type.toLowerCase();
        return collectionType;
      }
      return 'video';
    },
    getBackURL() {
      if (this.pageMode !== 'create') {
        return `/collections?type=${this.getRawCollectionType()}`;
      }
      return '/collections';
    },
    getCollectionType() {
      if (this.collection && this.collection.type) {
        const collectionType = this.collection.type.toLowerCase();
        switch (collectionType) {
          case 'video':
            return 'เพลย์ลิสต์';
          case 'playlist':
            return 'ชุดเพลย์ลิสต์';
          case 'episode':
            return 'ชุดตอน';
          case 'program':
            return 'ชุดรายการ';
          default:
            return 'คอลเล็คชั่น';
        }
      }
      return '';
    },
    deleteCollectionItems(item) {
      const updateCollectionItems = _.reject(this.collectionItems, (obj) => { return obj.itemId === item.itemId; });
      this.$store.dispatch('setCollectionItems', { payload: updateCollectionItems });
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
    hasNewItems() {
      return this.collection.hasNewItems;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/layouts/page.scss';
@import './src/assets/scss/themes/components/form.scss';
@import './src/assets/scss/themes/components/section-container.scss';
@import './src/assets/scss/themes/components/list-manage.scss';

.pagination-container {
  padding-top: $spacer * 2;
  display: flex;
  justify-content: center;
}

.sortable-ghost {
  opacity: 0.2;
}

.section.section-collection {
  background-color: transparent;
  box-shadow: none;

  .section-header {
    padding-left: 0;
    padding-right: 0;
    border: none;

    display: flex;

    .selector {
      margin-left: auto;
      flex: 0 0 60%;
    }
  }

  .section-sort {
    display: flex;
    margin-top: $spacer;
    margin-bottom: $spacer * 0.75;
    justify-content: flex-end;
    align-items: center;

    .name {
      margin-left: auto;
      margin-right: $spacer * 0.5;
    }
  }
}

.btn-pinned {
  .hover {
    display: none;
  }

  &:hover {
    .title {
      display: none;
    }

    .hover {
      display: block;
    }
  }
}

$bg-shadow: $gray-500;

.list-item-container {
  background-color: white;
  margin-bottom: $spacer * 0.75;
  padding-left: $spacer;
  border-radius: 3px;
  border: 1px solid white;

  &.draggable {
    box-shadow: 1px 1px 0px $bg-shadow, 2px 2px 0px $bg-shadow,
      3px 3px 0px $bg-shadow, 2px 2px 0px $bg-shadow, 2px 2px 0px $bg-shadow,
      2px 2px 0px $bg-shadow;

    &.list-item-container {
      cursor: move;
    }

    &.sortable-drag {
      cursor: move;
      cursor: -webkit-grabbing;
    }
  }

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: $gray-100;
    border-color: $primary;
  }
}

.page-header {

  .title {
    display: inline-flex;
    align-items: center;

    .badge-new {
      margin-left: 4px;
      padding: 6px 8px 4px;
      font-size: 50%;
    }
  }
}

@media (max-width: 575.98px) {
  .page-header {

    .title {
      display: inline-flex;
      flex-direction: column;
      align-items: flex-start;
      .badge-new {
        margin: 0;
      }
    }
  }

  .btn-delete {
    .label {
      display: none;
    }
  }

  .btn-pinned {
    display: none;
    // .label {
    //   display: none;
    // }
  }
}
</style>
